import { computed, ref } from 'vue'
import { getChatLimits } from '@/utils/chatApi'
import { estimateTokens } from '@/components/chat/mock/chatLimitsMock'

const plan = ref('guest')
const planName = ref('')
const requestsUsed = ref(0)
const requestsTotal = ref(3)
const tokensUsed = ref(0)
const tokensTotal = ref(0)
const allowed = ref(true)
const reason = ref(null)
const expiresAt = ref(null)
const daysLeft = ref(null)
const isTrial = ref(false)
const isPaid = ref(false)
// Флаги фич тарифа. Дефолт `true` для авторизованного, потому что старый бэк
// без этих полей не должен «отнимать» рабочие кнопки. Для гостя мы всё равно
// принудительно перекроем ниже (см. refreshLimits).
const allowShare = ref(true)
const allowExport = ref(true)
let limitsLoading = false


function isAuthenticatedNow() {
    try {
        return Boolean(localStorage.getItem('token'))
    } catch (error) {
        return false
    }
}

const isAuthenticated = ref(isAuthenticatedNow())
let pollTimer = null

if (typeof window !== 'undefined') {
    window.addEventListener('storage', () => {
        isAuthenticated.value = isAuthenticatedNow()
    })

    pollTimer = setInterval(() => {
        const next = isAuthenticatedNow()
        if (next !== isAuthenticated.value) {
            isAuthenticated.value = next
        }
    }, 1500)
}

if (typeof window !== 'undefined' && pollTimer) {
    window.addEventListener('beforeunload', () => clearInterval(pollTimer))
}

// Тариф платный или нет решает бэкенд (ChatPlan::isPaidTier), а не код тарифа:
// платных тарифов много (start, pro, enterprise, корпоративные).
const accessTier = computed(() => {
    if (!isAuthenticated.value) {
        return 'guest'
    }
    return isPaid.value ? 'paid' : 'free'
})

/** 0 в лимите запросов означает «без ограничений». */
const requestsUnlimited = computed(() => requestsTotal.value <= 0)

const remaining = computed(() =>
    requestsUnlimited.value ? 0 : Math.max(0, requestsTotal.value - requestsUsed.value),
)

const total = computed(() => requestsTotal.value)

const isLimitReached = computed(() => {
    if (allowed.value === false) {
        return true
    }
    return !requestsUnlimited.value && requestsUsed.value >= requestsTotal.value
})

function refreshAuthState() {
    isAuthenticated.value = isAuthenticatedNow()
    refreshLimits()
}

function consumeRequest() {
    // Источник правды — Laravel. После ответа обновляем лимиты с сервера,
    // локально больше не блокируем гостя по localStorage.
    refreshLimits()
}

function trackTokenUsage(amount) {
    if (!amount || amount <= 0) {
        return
    }
    if (isAuthenticated.value && isPaid.value) {
        tokensUsed.value = Math.min(tokensTotal.value, tokensUsed.value + amount)
    }
}

function resetForTesting() {
    requestsUsed.value = 0
    requestsTotal.value = 3
    allowed.value = true
    reason.value = null
    refreshLimits()
}

async function refreshLimits() {
    if (limitsLoading) return
    // Гостя больше нет как «работающего» тарифа: без токена не дёргаем
    // /chat/usage (бэк вернёт 401 после закрытия эндпоинта авторизацией) и
    // жёстко проставляем «доступ закрыт, требуется вход». UI это увидит и
    // покажет GuestOverlay + не даст ChatInputArea принимать ввод.
    if (!isAuthenticatedNow()) {
        plan.value = 'guest'
        planName.value = ''
        requestsUsed.value = 0
        requestsTotal.value = 0
        tokensUsed.value = 0
        tokensTotal.value = 0
        allowed.value = false
        reason.value = 'auth_required'
        expiresAt.value = null
        daysLeft.value = null
        isTrial.value = false
        isPaid.value = false
        // Гость не должен видеть кнопки как активные — оверлей и так поверх,
        // но на всякий случай сбрасываем флаги.
        allowShare.value = false
        allowExport.value = false
        return
    }
    limitsLoading = true
    try {
        const limits = await getChatLimits()
        plan.value = limits.plan || plan.value
        planName.value = limits.planName || ''
        requestsUsed.value = Number(limits.requestsUsed || 0)
        requestsTotal.value = Number(limits.requestsTotal || requestsTotal.value || 0)
        tokensUsed.value = Number(limits.tokensUsed || 0)
        tokensTotal.value = Number(limits.tokensTotal || 0)
        allowed.value = limits.allowed !== false
        reason.value = limits.reason || null
        expiresAt.value = limits.expiresAt || null
        daysLeft.value = limits.daysLeft ?? null
        isTrial.value = Boolean(limits.isTrial)
        isPaid.value = Boolean(limits.isPaid)
        // Флаги фич — приходят из /chat/usage (или /chat/plan как fallback).
        // Если бэк не отдаёт поле — chatApi.getChatLimits() подставит true,
        // чтобы старая версия UI работала как раньше.
        allowShare.value = limits.allowShare !== false
        allowExport.value = limits.allowExport !== false
    } catch (e) {
        // Если /usage временно недоступен для авторизованного, не блокируем чат.
        allowed.value = true
        reason.value = null
    } finally {
        limitsLoading = false
    }
}

export function useChatAccessMock() {
    refreshAuthState()
    refreshLimits()
    return {
        isAuthenticated,
        accessTier,
        remaining,
        total,
        isLimitReached,
        reason,
        plan,
        planName,
        tokensUsed,
        tokensTotal,
        requestsUsed,
        requestsTotal,
        expiresAt,
        daysLeft,
        isTrial,
        isPaid,
        allowShare,
        allowExport,
        consumeRequest,
        trackTokenUsage,
        refreshAuthState,
        refreshLimits,
        resetForTesting,
        estimateTokens,
    }
}
